import { PLAN_COPY, TRIAL_COPY } from "./plans";

export type LandingPlan = {
  id: "trial" | "personal" | "pro" | "enterprise";
  name: string;
  price: string;
  priceNote?: string;
  blurb: string;
  features: string[];
  cta: { label: string; href: string };
  badge?: string;
};

export const LANDING_PLANS: LandingPlan[] = [
  {
    id: "trial",
    name: TRIAL_COPY.name,
    price: TRIAL_COPY.price,
    priceNote: TRIAL_COPY.note,
    blurb: TRIAL_COPY.blurb,
    features: ["2.0M tokens", "1 project", "No card required"],
    cta: { label: "Start free trial", href: "/login" },
  },
  {
    id: "personal",
    name: PLAN_COPY.personal.name,
    price: PLAN_COPY.personal.price.month,
    priceNote: PLAN_COPY.personal.price.year,
    blurb: PLAN_COPY.personal.blurb,
    features: [
      "1 seat",
      "3 projects",
      "3.5M tokens per project-month",
      "2 free guest collaborators per project",
      "Up to 3 linked GitHub repositories",
    ],
    cta: { label: "Start free trial", href: "/login" },
  },
  {
    id: "pro",
    name: PLAN_COPY.pro.name,
    price: PLAN_COPY.pro.price.month,
    priceNote: PLAN_COPY.pro.price.year,
    blurb: PLAN_COPY.pro.blurb,
    badge: "Most popular",
    features: [
      "No seat minimum",
      "15 projects included",
      "8.0M tokens per project-month",
      "Team boards, presence and activity streams",
      "Team-wide decision engine",
      "Admin usage dashboard and roles",
    ],
    cta: { label: "Start free trial", href: "/login" },
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom pricing",
    blurb: "For organizations with compliance and scale requirements.",
    features: [
      "Unlimited projects",
      "BYOK or pooled tokens",
      "SAML/SSO and SCIM",
      "Zero data retention with LLM providers",
      "Audit logs and trace export",
      "99.9% uptime SLA",
    ],
    cta: {
      label: "Talk to us",
      href: "mailto:legal@flowicity.com?subject=Flowicity%20Enterprise",
    },
  },
];

export type DemoStory = {
  title: string;
  description: string;
  estimate: string;
  confidence: "high" | "medium" | "low";
  areas: string[];
};

export const DEMO: {
  initiative: string;
  grounding: string[];
  epic: { title: string; summary: string };
  stories: DemoStory[];
} = {
  initiative:
    "Add usage-based billing so teams pay for the AI tokens they actually use.",
  grounding: [
    "Read backend/src/services/usage_service.rs and 11 related files",
    "Read 2 documents: Pricing Architecture, Tenant Design",
    "Found 3 comparable items in the backlog",
    "Honouring decision: AI usage is billed per tenant, not per project",
  ],
  epic: {
    title: "Usage-based billing",
    summary:
      "Meter the AI tokens each tenant spends, bill them through Stripe, and surface the running total before the invoice lands.",
  },
  stories: [
    {
      title: "Meter AI token usage per tenant",
      description:
        "Record prompt and completion tokens against the tenant on every AI call, aggregated by calendar month.",
      estimate: "5 pts",
      confidence: "high",
      areas: ["services/usage"],
    },
    {
      title: "Expose tenant usage to the billing dashboard",
      description:
        "Serve the current month's token total and remaining budget from the tenant usage endpoint.",
      estimate: "3 pts",
      confidence: "high",
      areas: ["handlers/tenants"],
    },
    {
      title: "Report monthly usage to a Stripe metered price",
      description:
        "Push each tenant's monthly total to Stripe as a usage record on the metered subscription item.",
      estimate: "5 pts",
      confidence: "medium",
      areas: ["stripe"],
    },
    {
      title: "Warn in the shell as a tenant nears its budget",
      description:
        "Raise a banner in the app shell once a tenant passes 80% of its monthly token budget.",
      estimate: "2 pts",
      confidence: "medium",
      areas: ["components/layout"],
    },
  ],
};
